import React, { useMemo } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import { pluralize } from '../../lib/helpers/text.helpers';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { SEND_SKIP_REASON_ORDER } from './report-card-access.constants';
import { notifiedRecipientValues, sendSkipReasonLabels } from './report-card-access.helpers';
import { ReportCardAvailabilityMessages, SendPreviewOutcome } from './report-card-access.types';
import { useReportCardAvailabilityStyles } from './report-card-availability.styles';

interface ISendReportCardsPreviewProps {
  ageGroupName: string;
  preview?: IReportCardSendPreview;
  outcome: SendPreviewOutcome | null;
  error: boolean;
  messages: ReportCardAvailabilityMessages;
  nouns: { one: string; many: string };
  onRetry: () => void;
}

// Who a send would reach, from the same selection the send uses; or why it would reach nobody.
export const SendReportCardsPreview: React.FC<ISendReportCardsPreviewProps> = ({
  ageGroupName,
  preview,
  outcome,
  error,
  messages,
  nouns,
  onRetry,
}) => {
  const classes = useReportCardAvailabilityStyles();
  const skipLabels = useMemo(() => sendSkipReasonLabels(messages), [messages]);
  const players = (count: number) => pluralize(count, nouns.one, nouns.many);
  const contacts = (count: number) => pluralize(count, 'contact');

  if (error) {
    return (
      <div className={classes.inline}>
        <Typography variant="body2" color="error"><IntlMsg {...messages.errPreview} /></Typography>
        <Button size="small" color="primary" onClick={onRetry}><IntlMsg {...messages.btnRetry} /></Button>
      </div>
    );
  }
  if (!preview || !outcome) {
    return (
      <div className={classes.inline}>
        <CircularProgress size={16} />
        <Typography variant="body2"><IntlMsg {...messages.msgPreviewLoading} /></Typography>
      </div>
    );
  }
  if (outcome === 'REVOKED') {
    return <Typography variant="body2"><IntlMsg {...messages.msgSendRevoked} values={{ ageGroup: ageGroupName }} /></Typography>;
  }

  const { skipped } = preview;
  const skippedReasons = SEND_SKIP_REASON_ORDER.filter(reason => skipped[reason] > 0);

  return (
    <>
      {outcome === 'READY' && (
        <>
          <Typography variant="body1">
            <strong><IntlMsg {...messages.msgPreviewPlayers} values={{ count: preview.playerCount, players: players(preview.playerCount) }} /></strong>
          </Typography>
          <Typography variant="body2">
            <IntlMsg {...messages.msgPreviewNotified} values={notifiedRecipientValues(preview)} />
          </Typography>
          <Typography variant="body2">
            <IntlMsg {...messages.msgPreviewAccess} values={{ count: preview.accessContactCount, contacts: contacts(preview.accessContactCount) }} />
          </Typography>
          {preview.unreachablePlayerCount > 0 && (
            <Typography variant="body2">
              <IntlMsg
                {...messages.msgPreviewUnreachable}
                values={{
                  count: preview.unreachablePlayerCount,
                  players: players(preview.unreachablePlayerCount),
                  have: pluralize(preview.unreachablePlayerCount, 'has', 'have'),
                }}
              />
            </Typography>
          )}
        </>
      )}
      {outcome === 'NOTHING_NOT_CONCLUDED' && (
        <Typography variant="body2">
          <IntlMsg
            {...messages.msgNothingNotConcluded}
            values={{ count: skipped.notConcluded, players: players(skipped.notConcluded), have: pluralize(skipped.notConcluded, 'has', 'have') }}
          />
        </Typography>
      )}
      {outcome === 'NOTHING_ALL_SENT' && <Typography variant="body2"><IntlMsg {...messages.msgNothingAllSent} /></Typography>}
      {outcome === 'NOTHING_NO_DATA' && <Typography variant="body2"><IntlMsg {...messages.msgNothingNoData} /></Typography>}
      {outcome === 'NOTHING' && <Typography variant="body2"><IntlMsg {...messages.msgNothing} /></Typography>}

      {skippedReasons.length > 0 && (
        <div className={classes.section}>
          <Typography variant="body2"><IntlMsg {...messages.lblSkipped} /></Typography>
          <ul className={classes.skipped}>
            {skippedReasons.map(reason => (
              <li key={reason}>
                <Typography variant="body2">
                  <IntlMsg {...skipLabels[reason]} values={{ count: skipped[reason], players: players(skipped[reason]) }} />
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};
